import {QueryResult} from "@apollo/client";
import {Button, Dialog, InputGroup, Radio, RadioGroup, Switch, Tooltip} from "@blueprintjs/core";
import styled from "@emotion/styled";
import axios from "axios";
import {isValid} from "date-fns";
import {isArray} from "lodash";
import React, {ReactElement, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Address} from "../../../../domain/geocode-types";
import {DriverLocation, SearchDriverLocationQuery} from "../../../../generated/graphql";
import {extractSimplePref, PreferenceContext} from "../../../../providers/PreferenceProvider";
import {isCompleted} from "../../../../utils/stops/StopUtils";
import {getStopAddressText, getTimeZoneIdByAddress} from "../../../../utils/General";
import {ActionButton} from "../../../common/ConfirmationDialog";
import {Constants, TenantPreferences} from "../../../../constants/constants";
import {InformationIcon, WarningIcon} from "../../../common/icons";
import {getGPSStatusType, GPSStatusType} from "../../../map/driver/DriverMarkerV2";
import {TimeTag} from "../../../settings/ColorSettings/ColorizedIndicators/TriggerSelector";
import {
  DEFAULT_DWELL_MINUTES,
  MAX_DWELL_MINUTES,
  MIN_DWELL_MINUTES
} from "../../../settings/CompanySettings/common/constants";
import PlacesAutoCompleteInput from "../../../common/PlacesAutoCompleteInput";
import StopLocationSelector, {TStopLocation} from "./StopLocationSelector";
import TimePickerCommon from "./TimePickerCommon";
import {
  PRIMARY_BUTTON,
  PRIMARY_TEXT_COLOR,
  SECONDARY_TEXT_COLOR,
  distanceFromLeftOptimizeDialog
} from "./ManifestDialog.service";
import log from "loglevel";
import {findEarliestStopDateV2} from "../utils/ManifestDetails.utils";
import {getVehicleDwellTime} from "../../../../utils/VehicleUtils";
import {TVehicleConfiguration} from "../../../settings/VehicleConfiguration/VehicleConfiguration";
import {TManifestEnhanced, TManifestStopEnhanced} from "../../../common/types";

export interface OptimizeDialogProps {
  onClose: () => void;
  onSave: () => void;
  onDataChange: (data: OptimizeFormData) => void;
  homeAddress?: Address;
  manifest?: TManifestEnhanced;
  setIsOptimizeFromHomeLocation: React.Dispatch<React.SetStateAction<boolean>>;
  driverLocationResult?: QueryResult<SearchDriverLocationQuery>;
}

const convertToDateTimeLocalString = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export type OptimizeFormData = {
  ignoreTimeWindows?: boolean;
  startingLocation?: string;
  startingTime?: string;
  dwellTime?: number;
  endLocation?: string;
};

export type TOptimizationInfoItem = {
  title: string;
  required: boolean;
  subTitle?: string[];
  infoIcon?: {
    icon: ReactElement<any, any>;
    content: string | JSX.Element | undefined;
  };
};

type TOptimizationInfo = {
  startingLocation: TOptimizationInfoItem;
  dataTimeTrip: TOptimizationInfoItem;
  dwellTime: TOptimizationInfoItem;
  finalLocation: TOptimizationInfoItem;
};

enum LocationMessages {
  NotAvailable = "Driver's location isn't available",
  LatLngIssue = "Issues processing the lat/long of the most recent GPS point",
  OldGPS = "Driver's location data is at least 5m old and is unreliable",
  HomeNotAvailable = "Home Address Not Available"
}

const OptimizationsInfo: TOptimizationInfo = {
  startingLocation: {
    title: "Driver's Starting Location",
    required: true
  },
  dataTimeTrip: {
    title: "Start date and time",
    required: true,
    subTitle: ["Timezone of starting time is based on the timezone of the Driver's starting location"]
  },
  dwellTime: {
    title: "Dwell Time Average",
    required: true,
    infoIcon: {
      icon: <InformationIcon scale={0.7} fill={"grey"} />,
      content: (
        <div>
          <p style={{margin: "0"}}>Minimum: {MIN_DWELL_MINUTES} minute</p>
          <p style={{margin: "0"}}>Maximum: {MAX_DWELL_MINUTES} minutes</p>
        </div>
      )
    }
  },
  finalLocation: {
    title: "Final Location",
    required: false
  }
};

const OptimizeDialog: React.FC<OptimizeDialogProps> = ({
  onClose,
  onSave,
  onDataChange,
  homeAddress,
  manifest,
  setIsOptimizeFromHomeLocation,
  driverLocationResult
}) => {
  const {tenantPreferences} = useContext(PreferenceContext);
  const dwellTimeByVehicle = getVehicleDwellTime(
    manifest?.vehicleType.description,
    extractSimplePref(tenantPreferences, TenantPreferences.vehicleIconSetup).value as TVehicleConfiguration[]
  )?.toString();
  const initDwellTime =
    dwellTimeByVehicle ??
    Math.min(
      extractSimplePref(tenantPreferences, TenantPreferences.assumedOptimizationDwellTime, DEFAULT_DWELL_MINUTES)
        .value as number,
      MAX_DWELL_MINUTES
    ).toString();

  const isDispatcherIgnoreTimeWindows = extractSimplePref(
    tenantPreferences,
    TenantPreferences.dispatcherIgnoreTimeWindows,
    true
  ).value as boolean;

  const isDispatcherIgnoreTimeWindowOnFirstTry = extractSimplePref(
    tenantPreferences,
    TenantPreferences.dispatcherIgnoreTimeWindowsOnFirstTry,
    true
  ).value as boolean;

  const showIgnoreTimeWindowsToggle = isDispatcherIgnoreTimeWindows && isDispatcherIgnoreTimeWindowOnFirstTry;
  const isDispatcherIgnoreTimeWindowAsDefault = extractSimplePref(
    tenantPreferences,
    TenantPreferences.dispatcherIgnoreTimeWindowsAsDefault,
    false
  ).value as boolean;

  const [isIgnoreTimeWindows, setIsIgnoreTimeWindows] = useState(
    showIgnoreTimeWindowsToggle && isDispatcherIgnoreTimeWindowAsDefault
  );
  const [startLocation, setStartLocation] = useState("");
  const [timeZoneAddress, setTimeZoneAddress] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState("");
  const [dwellTime, setDwellTime] = useState(initDwellTime);
  const [endLocation, setEndLocation] = useState("");
  const [earliestDate, setEarliestDate] = useState<Date | null>(null);
  const [currentDriverLocationPlace, setCurrentDriverLocationPlace] = useState("");
  const [currentDriverLocationIsOld, setCurrentDriverLocationIsOld] = useState(true);
  const [isOptimizeEnabled, setIsOptimizeEnabled] = useState(false);
  const [isStartDateTimeEdited, setIsStartDateTimeEdited] = useState(false);

  useEffect(() => {
    if (startLocation) {
      getTimeZoneIdByAddress(startLocation)
        .then((res) => {
          if (res) {
            setTimeZoneAddress(res);
          } else {
            setTimeZoneAddress(undefined);
          }
        })
        .catch((error) => log.error(error));
    } else {
      setTimeZoneAddress(undefined);
    }
  }, [startLocation]);

  useEffect(() => {
    if (manifest && !isStartDateTimeEdited) {
      const findEarliestStopDate = findEarliestStopDateV2(manifest?.stops, {
        timezoneOfStartLocation: timeZoneAddress
      });
      setEarliestDate(findEarliestStopDate);
    }
  }, [isStartDateTimeEdited, manifest, timeZoneAddress]);

  useEffect(() => {
    onDataChange({
      ignoreTimeWindows: isIgnoreTimeWindows,
      startingLocation: startLocation,
      dwellTime: Number(dwellTime),
      startingTime: startDate,
      endLocation: endLocation
    });
  }, [startLocation, currentDriverLocationPlace, startDate, dwellTime, endLocation, onDataChange, isIgnoreTimeWindows]);

  useEffect(() => {
    if (!driverLocationResult?.loading) {
      const data = driverLocationResult?.data?.searchDriverLocations?.items as DriverLocation[];
      if (data && data.length > 0) {
        const driverLocation = data[0];
        const gpsStatusType = getGPSStatusType(driverLocation);
        setCurrentDriverLocationIsOld(gpsStatusType !== GPSStatusType.NEW);

        const lat = driverLocation.coordinates.split(",")[0];
        const lng = driverLocation.coordinates.split(",")[1];
        const response = axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&result_type=street_address&key=${Constants.GOOGLE_API_KEY}`
        );
        response.then((res) => {
          if (res.data.results.length > 0 && res.data.results[0].formatted_address) {
            setCurrentDriverLocationPlace(res.data.results[0].formatted_address);
          } else {
            setCurrentDriverLocationPlace(LocationMessages.LatLngIssue);
          }
        });
      } else {
        setCurrentDriverLocationPlace(LocationMessages.NotAvailable);
      }
    }
  }, [driverLocationResult?.data?.searchDriverLocations?.items, driverLocationResult?.loading]);

  const handleIgnoreTimeWindowsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsIgnoreTimeWindows(e.target.checked);
    },
    [setIsIgnoreTimeWindows]
  );

  const handleStartDateChange = useCallback((event: Date | null) => {
    if (!event) {
      setStartDate("");
    } else {
      setStartDate(convertToDateTimeLocalString(event));
    }
  }, []);

  const handleDwellTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDwellTime(event.target.value);
  };

  const handleCancel = () => {
    onClose();
    setEndLocation("");
    setStartLocation("");
    setStartDate("");
    setDwellTime("");
    setIsOptimizeEnabled(false);
    setTimeZoneAddress(undefined);
    setIsStartDateTimeEdited(false);
  };

  const handleOptimize = () => {
    onSave();
    handleCancel();
  };

  useEffect(() => {
    const isOptimizeEnable = Boolean(
      startLocation && isValid(new Date(startDate)) && dwellTime && Number(dwellTime) >= MIN_DWELL_MINUTES
    );
    setIsOptimizeEnabled(isOptimizeEnable);
  }, [startLocation, startDate, dwellTime]);

  useEffect(() => {
    return () => {
      handleCancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledDialog isOpen={true} onClose={onClose} distanceFromLeft={distanceFromLeftOptimizeDialog()}>
      <DialogContainer data-testid="optimization-dialog-container">
        <DialogHeader>
          <DialogTitle data-testid="dialog-title">MANIFEST OPTIMIZATION</DialogTitle>
          <DialogCloseIcon variant="minimal" data-testid="dialog-close-icon" onClick={onClose} icon="cross" />
        </DialogHeader>
        <DialogBody>
          {showIgnoreTimeWindowsToggle && (
            <Switch
              checked={isIgnoreTimeWindows}
              onChange={handleIgnoreTimeWindowsChange}
              label="Ignore Time Windows"
              style={{marginBottom: "0", fontSize: "16px", width: "fit-content"}}
              alignIndicator="right"
            />
          )}
          <OptimizationOptions
            optionInfo={OptimizationsInfo.startingLocation}
            renderer={
              <ProvideLocation
                homeAddress={homeAddress}
                currentAddress={currentDriverLocationPlace}
                type={ProvideLocationType.starting}
                onChange={setStartLocation}
                currentDriverLocationIsOld={currentDriverLocationIsOld}
                manifestStops={manifest?.stops ?? []}
                setIsOptimizeFromHomeLocation={setIsOptimizeFromHomeLocation}
              />
            }
          />
          <OptimizationOptions
            optionInfo={OptimizationsInfo.dataTimeTrip}
            renderer={
              <StartDateTime
                timeZoneAddress={timeZoneAddress}
                onChange={handleStartDateChange}
                earliestDate={earliestDate}
                setIsStartDateTimeEdited={setIsStartDateTimeEdited}
              />
            }
          />
          <OptimizationOptions
            optionInfo={OptimizationsInfo.dwellTime}
            renderer={<DwellTimeInput value={dwellTime} handleChangeDwell={handleDwellTimeChange} min={0} max={60} />}
          />
          <OptimizationOptions
            optionInfo={OptimizationsInfo.finalLocation}
            renderer={
              <ProvideLocation
                type={ProvideLocationType.final}
                startLocation={startLocation}
                homeAddress={homeAddress}
                onChange={setEndLocation}
              />
            }
          />
        </DialogBody>
        <Footer>
          <ActionButton data-testid="dialog-cancel-button" onClick={handleCancel}>
            Cancel
          </ActionButton>
          <ActionButton
            data-testid="dialog-optimize-button"
            disabled={!isOptimizeEnabled}
            onClick={handleOptimize}
            style={{
              color: "white",
              background: !isOptimizeEnabled ? "#d9d9d9" : "linear-gradient(180deg, #214A89 0%, #14305A 100%)",
              width: "92px"
            }}
            text="Optimize"
          />
        </Footer>
      </DialogContainer>
    </StyledDialog>
  );
};

export default OptimizeDialog;
interface DialogContainerProps {
  distanceFromLeft: number;
}
interface OptimizationOptionsProps {
  renderer: any;
  optionInfo: TOptimizationInfoItem;
}
export const OptimizationOptions = ({optionInfo, renderer}: OptimizationOptionsProps) => {
  const {title, required, subTitle, infoIcon} = optionInfo;
  return (
    <OptimizationOptionsContainer
      data-testid={`optimize-option-container-${title.toLocaleLowerCase().replaceAll(" ", "-")}`}
      subTitle={subTitle}
    >
      <OptimizationTitle data-testid="optimize-option-title">
        {title}
        {infoIcon && (
          <Tooltip content={infoIcon.content} placement={"right"}>
            <OptimizationIconContainer>{infoIcon.icon}</OptimizationIconContainer>
          </Tooltip>
        )}
        {!required && <OptimizationRequired data-testid="optimize-option-optional">(optional)</OptimizationRequired>}
      </OptimizationTitle>
      {isArray(subTitle) && subTitle.length > 0 && (
        <>
          {subTitle?.map((x) => (
            <OptimizationSubTitle key={x} data-testid="optimize-option-subtitle">
              {x}
            </OptimizationSubTitle>
          ))}
        </>
      )}
      {renderer}
    </OptimizationOptionsContainer>
  );
};

export enum ProvideLocationType {
  starting,
  final
}
export interface ProvideLocationProps {
  type: ProvideLocationType;
  onChange: React.Dispatch<React.SetStateAction<string>>;
  currentAddress?: string;
  homeAddress?: Address;
  currentDriverLocationIsOld?: boolean;
  manifestStops?: TManifestStopEnhanced[];
  startLocation?: string;
  setIsOptimizeFromHomeLocation?: React.Dispatch<React.SetStateAction<boolean>>;
}
type SelectTypeState = "home" | "current" | "custom" | "stop" | "start" | "";

export const ProvideLocation = ({
  type,
  onChange,
  currentAddress,
  homeAddress,
  currentDriverLocationIsOld,
  manifestStops,
  startLocation,
  setIsOptimizeFromHomeLocation
}: ProvideLocationProps) => {
  const [selectTypeLocation, setSelectTypeLocation] = useState<SelectTypeState>("");
  const [isDisablePlaceAuto, setIsDisablePlaceAuto] = useState(true);
  const [placeInputPlace, setPlaceInputPlace] = useState("");
  const [selectedStopLocation, setSelectedStopLocation] = React.useState<TStopLocation | undefined>();

  const stopLocationOptions: TStopLocation[] | undefined = useMemo(
    () =>
      manifestStops
        ?.filter((stop) => !isCompleted(stop))
        .sort((a, b) => a.manifestSequence - b.manifestSequence)
        .map((stop) => {
          return {
            key: stop.jobStopId,
            address: getStopAddressText({
              stop,
              hasDispatchZone: true
            }),
            stopType: stop.stopType,
            name: stop.name ?? ""
          };
        }) ?? [],
    [manifestStops]
  );

  const handleSelectPlaceInput = useCallback(
    (e: string) => {
      setPlaceInputPlace(e);
      onChange(e);
    },
    [setPlaceInputPlace, onChange]
  );

  const handleSelectStopLocation = useCallback(
    (item: TStopLocation) => {
      setSelectedStopLocation(item);
      onChange(item.address ?? "");
    },
    [onChange]
  );

  const handleSelectType = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const typeOfLocation = e.currentTarget.value as SelectTypeState;
      setSelectTypeLocation(typeOfLocation);
      if (typeOfLocation === "home") {
        setPlaceInputPlace(homeAddress?.formatAddress() ?? "");
        setIsDisablePlaceAuto(true);
        onChange(homeAddress?.formatAddress() ?? "");
        setIsOptimizeFromHomeLocation?.(true);
      }
      if (typeOfLocation === "current") {
        setPlaceInputPlace(currentAddress ?? "");
        setIsDisablePlaceAuto(true);
        onChange(currentAddress ?? "");
        setIsOptimizeFromHomeLocation?.(false);
      }
      if (typeOfLocation === "stop") {
        setPlaceInputPlace("");
        setIsDisablePlaceAuto(true);
        onChange(selectedStopLocation?.address ?? "");
        setIsOptimizeFromHomeLocation?.(false);
      }
      if (typeOfLocation === "custom") {
        setPlaceInputPlace("");
        setIsDisablePlaceAuto(false);
        onChange("");
        setIsOptimizeFromHomeLocation?.(false);
      }
      if (typeOfLocation === "start") {
        setPlaceInputPlace(startLocation ?? "");
        setIsDisablePlaceAuto(true);
        onChange(startLocation ?? "");
      }
    },
    [homeAddress, onChange, setIsOptimizeFromHomeLocation, currentAddress, selectedStopLocation?.address, startLocation]
  );

  const clearSelection = useCallback(() => {
    setSelectTypeLocation("");
    setPlaceInputPlace("");
    onChange("");
    setIsDisablePlaceAuto(true);
  }, [onChange]);

  const handleClickRadio = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      if (
        type === ProvideLocationType.final &&
        event.currentTarget.value === selectTypeLocation &&
        selectTypeLocation !== ""
      ) {
        clearSelection();
      }
    },
    [clearSelection, selectTypeLocation, type]
  );

  const renderTooltipContent = useCallback(() => {
    if (!currentAddress) return LocationMessages.NotAvailable;
    if ([LocationMessages.LatLngIssue, LocationMessages.NotAvailable].includes(currentAddress as any))
      return currentAddress;
    if (currentDriverLocationIsOld) {
      return LocationMessages.OldGPS;
    }
  }, [currentAddress, currentDriverLocationIsOld]);

  const isDisableCurrentLocationOption = useMemo(() => {
    return (
      !currentAddress ||
      [LocationMessages.LatLngIssue, LocationMessages.NotAvailable].includes(currentAddress as any) ||
      currentDriverLocationIsOld
    );
  }, [currentAddress, currentDriverLocationIsOld]);

  useEffect(() => {
    if (selectTypeLocation === "current" && currentDriverLocationIsOld) {
      clearSelection();
    }
  }, [clearSelection, currentDriverLocationIsOld, selectTypeLocation]);

  useEffect(() => {
    if (selectTypeLocation === "start") {
      if (!startLocation) {
        clearSelection();
      } else {
        setPlaceInputPlace(startLocation);
        onChange(startLocation);
      }
    }
  }, [clearSelection, onChange, selectTypeLocation, startLocation]);

  return (
    <RadioGroupCustom onChange={handleSelectType} selectedValue={selectTypeLocation}>
      <Radio value={"home"} onClick={handleClickRadio} disabled={!homeAddress}>
        <DialogText primary={Boolean(homeAddress)}>Home</DialogText>{" "}
        <DialogText className="optimize-subtext">
          {`( ${homeAddress?.formatAddress() ?? LocationMessages.HomeNotAvailable} )`}
        </DialogText>
      </Radio>
      {type === ProvideLocationType.starting && (
        <Radio disabled={isDisableCurrentLocationOption} value={"current"}>
          <DialogText primary={!isDisableCurrentLocationOption}>Current Location</DialogText>
          {isDisableCurrentLocationOption && (
            <Tooltip
              className="custom-tooltip"
              content={renderTooltipContent()}
              placement="left"
              transitionDuration={0}
            >
              <WarningIcon />
            </Tooltip>
          )}
        </Radio>
      )}
      {type === ProvideLocationType.starting && (
        <Radio value={"stop"}>
          <DialogText primary>Stop Location from Manifest</DialogText>
          {selectTypeLocation === "stop" && (
            <StopLocationSelector
              items={stopLocationOptions}
              selectedItem={selectedStopLocation}
              onSelectItem={handleSelectStopLocation}
            />
          )}
        </Radio>
      )}
      {type === ProvideLocationType.final && (
        <Radio onClick={handleClickRadio} value={"start"} disabled={!startLocation}>
          <DialogText primary={Boolean(startLocation)}>Start Location</DialogText>{" "}
          <DialogText className="optimize-subtext">{startLocation ? `( ${startLocation} )` : ""}</DialogText>
        </Radio>
      )}
      <RadioCustomContainer onChange={handleSelectType} selectedValue={selectTypeLocation}>
        <Radio onClick={handleClickRadio} value={"custom"} className="custom-radio">
          <DialogText primary>Custom</DialogText>
        </Radio>
        {selectTypeLocation && selectTypeLocation !== "stop" && (
          <PlacesAutoCompleteInput
            value={placeInputPlace}
            onChange={handleSelectPlaceInput}
            disabled={isDisablePlaceAuto}
          />
        )}
      </RadioCustomContainer>
    </RadioGroupCustom>
  );
};
export interface StartDateTimeProps {
  onChange: (...arg: any) => void;
  earliestDate: Date | null;
  timeZoneAddress: string | undefined;
  setIsStartDateTimeEdited: React.Dispatch<React.SetStateAction<boolean>>;
}

export const StartDateTime = ({
  onChange,
  earliestDate,
  timeZoneAddress,
  setIsStartDateTimeEdited
}: StartDateTimeProps) => {
  return (
    <TimePickerCommon
      onChange={onChange}
      earliestDate={earliestDate}
      timeZoneAddress={timeZoneAddress}
      setIsStartDateTimeEdited={setIsStartDateTimeEdited}
    />
  );
};
export interface DwellTimePickerProps {
  value: string;
  min: number;
  max: number;
  handleChangeDwell: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const DwellTimeInput = ({value, handleChangeDwell, min, max}: DwellTimePickerProps) => {
  const [openWarningToolTip, setOpenWarningToolTip] = useState(false);

  const onChangeDwell = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (Number(e.target.value) < min || Number(e.target.value) > max || isNaN(Number(e.target.value))) return;
      if (Number(e.target.value) < MIN_DWELL_MINUTES && e.target.value !== "") {
        setOpenWarningToolTip(true);
      } else {
        setOpenWarningToolTip(false);
      }
      handleChangeDwell(e);
    },
    [handleChangeDwell, min, max]
  );
  return (
    <DwellTimeContainer>
      <Tooltip
        isOpen={openWarningToolTip}
        intent="danger"
        content={`Dwell time must be at least ${MIN_DWELL_MINUTES} minute`}
        placement="top"
      >
        <DwellInput
          data-testid="dwell-input"
          type="number"
          placeholder="5"
          value={value}
          onChange={onChangeDwell}
          rightElement={<TimeTag>min</TimeTag>}
        />
      </Tooltip>
    </DwellTimeContainer>
  );
};

const DwellInput = styled(InputGroup)`
  width: fit-content;

  .bp5-input {
    width: 184px;
    height: 32px;
    border-radius: 4px;
    border: 1px solid #c3c3c36e;
    box-shadow: none;
    padding: 0 12px;

    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    ::placeholder {
      color: ${SECONDARY_TEXT_COLOR};
    }
  }

  .bp5-tag {
    margin-right: 12px;
  }
`;

const DwellTimeContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const CloseIcon = () => {
  return (
    <svg
      data-testid="close-icon"
      width="10"
      height="11"
      viewBox="0 0 10 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.73773 2.19658C9.0893 1.80419 9.0893 1.16694 8.73773 0.774549C8.38617 0.382157 7.81523 0.382157 7.46367 0.774549L4.50211 4.0832L1.53773 0.777688C1.18617 0.385296 0.615234 0.385296 0.263672 0.777688C-0.0878906 1.17008 -0.0878906 1.80732 0.263672 2.19972L3.22805 5.50522L0.266484 8.81387C-0.0850781 9.20626 -0.0850781 9.84351 0.266484 10.2359C0.618047 10.6283 1.18898 10.6283 1.54055 10.2359L4.50211 6.92725L7.46648 10.2328C7.81805 10.6252 8.38898 10.6252 8.74055 10.2328C9.09211 9.84037 9.09211 9.20312 8.74055 8.81073L5.77617 5.50522L8.73773 2.19658Z"
        fill="#161616"
      />
    </svg>
  );
};

export const StyledDialog = styled(Dialog)<DialogContainerProps>`
  position: absolute;
  left: ${(props) => props.distanceFromLeft && `${props.distanceFromLeft}px`};
  width: 485px;
  &.bp5-dialog {
    background-color: #fcfcfc;
    border-radius: 0;
    border-radius: 16px;
    font-family: Roboto, sans-serif;
  }
`;

export const DialogContainer = styled.div`
  padding: 24px;
`;
export const DialogHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
export const DialogBody = styled.div<{gap?: number}>`
  padding: 24px 8px 32px 0;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.gap ?? 32}px;
  justify-content: space-between;
  max-height: calc(100vh - 150px);
  overflow-y: auto;
`;

export const Footer = styled.div`
  display: flex;
  justify-content: right;
  gap: 10px;
`;

export const DialogTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #797979;
  line-height: 1;
  margin: 0;
`;
export const DialogCloseIcon = styled(Button)``;

interface OptimizationOptionsContainerProps {
  subTitle?: string[];
}
const OptimizationOptionsContainer = styled.div<OptimizationOptionsContainerProps>`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const OptimizationTitle = styled.div`
  font-weight: 400;
  font-size: 16px;
  color: ${PRIMARY_TEXT_COLOR};

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const OptimizationIconContainer = styled.div`
  transform: translateY(2px);
`;

const OptimizationSubTitle = styled.div`
  padding: 0;
  color: ${SECONDARY_TEXT_COLOR};
  font-size: 12px;
`;
const OptimizationRequired = styled.span`
  color: ${SECONDARY_TEXT_COLOR};
`;

const RadioGroupCustom = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .bp5-control input:checked ~ .bp5-control-indicator {
    background-color: ${PRIMARY_BUTTON} !important;
  }
  .bp5-control input:checked ~ span {
    color: ${PRIMARY_TEXT_COLOR};
  }
  .custom-tooltip {
    position: absolute;
    right: 0;
    bottom: 0;
  }

  .bp5-radio {
    margin: 0;
  }
`;
const RadioCustomContainer = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  height: fit-content;
`;

export const DialogText = styled.span<{primary?: boolean}>`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => (props.primary ? `${PRIMARY_TEXT_COLOR}` : `${SECONDARY_TEXT_COLOR}`)};
  &.optimize-subtext {
    color: ${SECONDARY_TEXT_COLOR} !important;
  }
  .bp5-button {
    padding: 0 5px 0 5px;
    vertical-align: baseline;
  }
`;
